/**
 * WhatsNewTicker — admin-curated "look at us shipping" feed.
 *
 * Pairs with LaunchBanner: the banner says something is coming, the ticker
 * proves it. Pass holders, judges, and casual visitors see real recent work
 * landing in real time. Methodology hold: invitation, not transaction.
 *
 * Hides automatically when the launch phase is `retired` or the entries
 * list is empty (don't render an empty container — silence > noise).
 */
import { useEffect, useState } from 'react';
import { Hammer } from 'lucide-react';
import { launchAPI } from '../lib/api';

function formatEntryDate(iso) {
  // Compact "Apr 27" style date — calm, not urgent.
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

export default function WhatsNewTicker({ compact = false }) {
  const [status, setStatus] = useState(null);

  useEffect(() => {
    let mounted = true;
    launchAPI.status()
      .then((r) => { if (mounted) setStatus(r.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  if (!status || status.phase === 'retired') return null;
  const entries = Array.isArray(status.entries) ? status.entries : [];
  if (entries.length === 0) return null;

  return (
    <div
      className={`glass-panel ${compact ? 'p-3' : 'p-4'} border border-[#2F8A92]/20`}
      data-testid="whats-new-ticker"
    >
      <div className="flex items-center gap-2 mb-2">
        <Hammer className="text-[#2F8A92]" size={16} />
        <h3 className="font-hud text-[#2F8A92] text-xs md:text-sm tracking-[0.25em] uppercase">
          What's New
        </h3>
        <span className="font-mono text-xs md:text-sm text-white/65 ml-auto">
          {entries.length} recent
        </span>
      </div>
      <ul className="space-y-2.5" data-testid="whats-new-ticker-list">
        {entries.map((entry, idx) => (
          <li
            key={entry.id}
            className="flex items-start gap-3 group"
            data-testid={`whats-new-entry-${idx}`}
          >
            <span className="font-hud text-xs md:text-sm text-[#D4A84A] tracking-wider flex-shrink-0 mt-0.5 min-w-[52px]">
              {formatEntryDate(entry.entry_date)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="font-mono text-sm md:text-base text-white leading-snug">
                {entry.title}
              </div>
              {entry.description && (
                <p className="font-mono text-sm md:text-base text-white/75 mt-1 leading-snug">
                  {entry.description}
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
      <p className="font-mono text-xs md:text-sm text-white/55 mt-3 italic">
        Curated by the build team. Anti-SaaS, anti-noise.
      </p>
    </div>
  );
}
